import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

/**
 * DeepLinkRedirect Component
 * Handles deep linking to Yoospeak app or app stores
 * Replicates functionality from deepLink.hbs template
 * 
 * Supports multiple formats:
 * - /video/:id
 * - /teacher/:id
 * - /student/:id
 * - /community/:id
 * - /live/:id
 * - /deeplink/callback?resourceType=...&id=...
 */
export const DeepLinkRedirect = () => {
  const location = useLocation();
  const params = useParams();
  const [deepLinkData, setDeepLinkData] = useState({
    videoId: '',
    liveId: '',
    communityId: '',
    userId: '',
    userType: '',
    resourceType: 'video',
    title: 'Yoospeak - Learn & Earn',
    description: 'Open in Yoospeak app'
  });

  useEffect(() => {
    // Parse query parameters
    const queryParams = new URLSearchParams(location.search);
    
    let videoId = queryParams.get('videoId') || '';
    let liveId = queryParams.get('liveId') || '';
    let communityId = queryParams.get('communityId') || '';
    let userId = queryParams.get('userId') || '';
    let userType = queryParams.get('userType') || '';
    let resourceType = queryParams.get('resourceType') || 'video';
    const title = queryParams.get('title') || 'Yoospeak - Learn & Earn';
    const description = queryParams.get('description') || 'Open in Yoospeak app';

    // Extract resource type and ID from path
    if (location.pathname.includes('/teacher/')) {
      resourceType = 'profile';
      userType = 'teacher';
      userId = params.id || '';
    } else if (location.pathname.includes('/student/')) {
      resourceType = 'profile';
      userType = 'student';
      userId = params.id || '';
    } else if (location.pathname.includes('/community/')) {
      resourceType = 'community';
      communityId = params.id || '';
    } else if (location.pathname.includes('/video/')) {
      resourceType = 'video';
      videoId = params.id || '';
    } else if (location.pathname.includes('/live/')) {
      resourceType = 'live';
      liveId = params.id || '';
    }

    setDeepLinkData({
      videoId,
      liveId,
      communityId,
      userId,
      userType,
      resourceType,
      title,
      description
    });
  }, [location, params]);

  const handleOpenApp = () => {
    const { videoId, liveId, communityId, userId, userType, resourceType } = deepLinkData;
    
    // Detect device type
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isChrome = /Chrome\//i.test(navigator.userAgent) && 
                     !/Edg\//i.test(navigator.userAgent) && 
                     !/OPR\//i.test(navigator.userAgent);

    // Build deeplink path
    let deepLinkPath = '';
    if (resourceType === 'video' && videoId) {
      deepLinkPath = `video?id=${encodeURIComponent(videoId)}`;
    } else if (resourceType === 'live' && liveId) {
      deepLinkPath = `live?id=${encodeURIComponent(liveId)}`;
    } else if (resourceType === 'community' && communityId) {
      deepLinkPath = `community?id=${encodeURIComponent(communityId)}`;
    } else if (resourceType === 'profile' && userId && userType) {
      deepLinkPath = `${userType}?id=${encodeURIComponent(userId)}`;
    }

    const customScheme = deepLinkPath ? `yoospeak://${deepLinkPath}` : 'yoospeak://';
    const androidIntent = deepLinkPath
      ? `intent://${deepLinkPath}#Intent;scheme=yoospeak;package=com.yoospeak.learning;launchFlags=268435456;end`
      : `intent://#Intent;scheme=yoospeak;package=com.yoospeak.learning;launchFlags=268435456;end`;

    try {
      if (isAndroid && isChrome) {
        window.location.href = androidIntent;
      } else if (isAndroid) {
        window.location.href = customScheme;
      } else if (isIOS) {
        // Use hidden anchor for iOS reliability
        const a = document.createElement('a');
        a.href = customScheme;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.location.href = customScheme;
        }, 300);
      }
    } catch (e) {
      console.log('Failed to open app:', e);
    }
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      margin: 0
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        padding: '40px 30px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#333', fontSize: '22px', marginBottom: '10px' }}>
          Yoospeak App
        </h2>
        <p style={{ color: '#666', fontSize: '14px', lineHeight: 1.6, margin: '15px 0' }}>
          Tap the button below to open the app. If you don't have the app, you can install it from the store links below.
        </p>

        <button
          onClick={handleOpenApp}
          style={{
            display: 'inline-block',
            padding: '14px 32px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '16px',
            border: 'none',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            marginTop: '20px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 10px 25px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Open in App
        </button>

        <div style={{
          marginTop: '30px',
          paddingTop: '30px',
          borderTop: '1px solid #eee'
        }}>
          <p style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
            Don't have the app? Get it from:
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <a
              href="https://apps.apple.com/in/app/yoospeak/id6746839854"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', margin: '0 10px 10px 0' }}
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                alt="Download on App Store"
                style={{ height: '40px', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.yoospeak.learning"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', margin: '0 10px 10px 0' }}
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                style={{ height: '40px', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
